import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

import { isSupportedByLinkingPlatform, type RegexStorage } from '../../linking-platform/utils';

const linkPatternStart = '\\bhttps?:\\/\\/(?:www\\.)?';
const linkPatternEnd = '[^\\s\\]\\)]*';

const linkRegex = {
	youtube: `${linkPatternStart}youtube\\.com${linkPatternEnd}`,
	youtubeShortUrl: `${linkPatternStart}youtu\\.be${linkPatternEnd}`,
	loomVideo: `${linkPatternStart}loom\\.com\\/share\\/${linkPatternEnd}`,
	figmaFile: `${linkPatternStart}figma\\.com\\/(file|design)\\/${linkPatternEnd}`,
	confluenceWhiteboard: `${linkPatternStart}.*\\/wiki\\/spaces\\/.*\\/whiteboard\\/${linkPatternEnd}`,
	googlePresentationAndSpreadsheet: `${linkPatternStart}docs\\.google\\.com\\/(presentation|spreadsheets)\\/d\\/${linkPatternEnd}`,
	googleDoc: `${linkPatternStart}docs\\.google\\.com\\/(document)\\/d\\/${linkPatternEnd}`,
	googleForm: `${linkPatternStart}docs\\.google\\.com\\/forms\\/d\\/${linkPatternEnd}`,
	microsoftForm: `${linkPatternStart}forms\\.office\\.com\\/Pages\\/ResponsePage\\.aspx\\?id\\=${linkPatternEnd}`, // this will be removed following project
	analyticsChart: `\\bhttps://[^.]+\\.(atlassian\\.net|jira-dev\\.com)/analytics/[^/]+/chart-${linkPatternEnd}`, // this will be removed following project
};

const newThirdPartyLinkRegex = {
	github: `${linkPatternStart}github\\.com${linkPatternEnd}`,
	canvaDesign: `${linkPatternStart}canva\\.com\\/design\\/[a-zA-Z0-9_\\-.]{1,255}\\/[a-zA-Z0-9_\\-.]{1,255}(?:\\/[^\\s\\]\\)]*)?`,
	canvaShortLink: `${linkPatternStart}canva\\.link\\/[a-zA-Z0-9]+(?:\\?${linkPatternEnd})?`,
	rovo: `${linkPatternStart}[^\\s\\]\\)]*\\.atlassian\\.net\\/rovo${linkPatternEnd}`,
	amplitude: `${linkPatternStart}app\\.amplitude\\.com${linkPatternEnd}`,
};

const buildEmbedRegex = (): RegExp =>
	new RegExp(
		[...Object.values(linkRegex), ...Object.values(newThirdPartyLinkRegex)].join('|'),
		'g',
	);

const embedInlineRegex = [
	// avpRovoSkillsChart
	`${linkPatternStart}avp-rovo-skills\\.[-\\w]+.frontend.public.atl-paas.net/v1/chart[^\\s\\)]+${linkPatternEnd}`,
];

const avpChartsRegex = `${linkPatternStart}.*?\\/avpviz\\/c\\/[^\\/]+.*${linkPatternEnd}`;

/**
 * Checks if there is more than one link in the token stream.
 * Used to determine if all links should be rendered inline instead of as embeds.
 */
const hasMultipleLinks = (tokens: Token[]): boolean => {
	let linkCount = 0;

	for (const token of tokens) {
		if (token.type !== 'inline' || !token.children) {
			continue;
		}

		for (const childToken of token.children) {
			if (childToken?.type === 'link_open') {
				linkCount++;
				if (linkCount > 1) {
					return true; // Early exit once we find more than one
				}
			}
		}
	}

	return false;
};

const createSmartLinks =
	({
		regexStorage,
		shouldShowAsInline,
		xpcWrapUrl,
	}: {
		regexStorage: RegexStorage;
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean;
		shouldShowAsInline?: boolean;
		xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
	}) =>
	(state: StateCore): void => {
		const embedRegex = buildEmbedRegex();
		const tokens: Token[] = [];
		const embedTokens: Token[] = [];

		let embedMatches: string[] = [];
		let filteredTokens: Token[] = [];
		const renderedInlineEmbeds = new Set<string>();

		// If there are more than one link or the user opened the chat in mini modal, all links display inline
		const forceAllToInline = hasMultipleLinks(state.tokens) || shouldShowAsInline;

		// process each token to determine if the link should be converted to a smartlink
		for (const token of state.tokens) {
			let processedTokenChildren: Array<Token> = [];

			const regexMatchesRaw = token.content?.trim().match(embedRegex);
			const regexMatches: string[] = regexMatchesRaw ? Array.from(new Set(regexMatchesRaw)) : [];
			embedMatches = [...embedMatches, ...regexMatches];

			// Any block or embed Smart Card links will be in an "inline" token. If
			// the current token is anything other than "inline", or there's no child tokens
			// to process, then we add it to the list of tokens to keep and move forward
			if (token.type !== 'inline' || !token.children) {
				tokens.push(token);
				continue;
			}

			var outputToken = new state.Token('inline', '', 0);
			let tokensToAppend: Token[] = [outputToken];

			if (token.children) {
				let discardUntilIndex = -1;

				token.children.forEach((childToken, i) => {
					const discardedToken =
						i <= discardUntilIndex && ['link_close', 'text'].includes(childToken.type);

					if (discardedToken) {
						return;
					}

					const url = childToken.attrGet('href') ?? '';
					const isInlineLink = url === token.children?.[i + 1]?.content;

					if (
						childToken?.type === 'link_open' &&
						(isSupportedByLinkingPlatform(url, regexStorage) || isInlineLink)
					) {
						// Discard the next two tokens. We're now processing the opening node for a URL
						// that will result in a new node that replaces it, therefore the subsequent link content and
						// closing node are no longer needed
						discardUntilIndex = i + 2;

						// This link will be rendered as a smart link (embed or inline card) by the linking
						// platform, so we only strip the `datasource` param without applying full XPC wrapping.
						const smartLinkUrl = xpcWrapUrl ? xpcWrapUrl(url, true) : url;

						// Check if this link should be rendered as an embed card inline (e.g., Loom, YouTube, Figma) at end of result
						const matchesEmbedRegex = url.match(embedRegex);

						if (matchesEmbedRegex && !embedMatches.includes(url)) {
							embedMatches.push(url);
						}

						// handle condition for when link in the token content matches the linkRegex
						const shouldRenderLinksAsEmbed = matchesEmbedRegex;

						const shouldRenderAvpRovoSkillsEmbedInline =
							token.level <= 1 && embedInlineRegex.some((r) => url.match(r));

						const shouldRenderAvpChartsEmbedInline = token.level <= 1 && url.match(avpChartsRegex);

						const shouldRenderAvpEmbedInline =
							shouldRenderAvpRovoSkillsEmbedInline || shouldRenderAvpChartsEmbedInline;

						// Render inline embed if either regex matches and token content is not part of a list
						const shouldRenderEmbedInline = shouldRenderLinksAsEmbed && token.level <= 1;

						if (shouldRenderAvpEmbedInline || shouldRenderLinksAsEmbed) {
							// handle case if it is an analytic chart link or microsoft form link
							// these should be rendered as inline cards, not embed cards
							const isMicrosoftFormLink = new RegExp(linkRegex.microsoftForm).test(url);
							const isAnalyticsChartLink = new RegExp(linkRegex.analyticsChart).test(url);

							if (
								(!forceAllToInline &&
									shouldRenderEmbedInline &&
									!isMicrosoftFormLink &&
									!isAnalyticsChartLink) ||
								shouldRenderAvpEmbedInline
							) {
								outputToken.children = processedTokenChildren;
								tokensToAppend.push(new state.Token('paragraph_close', '', 0));

								// Capture whether the URL was standalone on its own line
								// (i.e. there was no preceding text in the same paragraph)
								const isStandaloneUrl = processedTokenChildren.length === 0;

								processedTokenChildren = [];

								let embedToken = new state.Token('embedCard', '', 0);
								embedToken.content = '';
								embedToken.block = true;
								embedToken.attrSet('url', smartLinkUrl);
								embedToken.attrSet('layout', 'center');
								// Mark whether this embed came from a standalone-line URL
								if (isStandaloneUrl) {
									embedToken.attrSet('isStandalone', 'true');
								}
								tokensToAppend.push(embedToken);

								tokensToAppend.push(new state.Token('paragraph_open', '', 0));

								outputToken = new state.Token('inline', '', 0);
								tokensToAppend.push(outputToken);

								// Track that we've rendered this URL as an inline embed
								renderedInlineEmbeds.add(url);

								return;
							}
						}

						// create an inline card for the links that don't qualify
						let cardToken = new state.Token('inlineCard', '', 0);
						cardToken.content = '';
						cardToken.block = false;
						cardToken.level = childToken.level;
						cardToken.attrSet('url', smartLinkUrl);
						processedTokenChildren.push(cardToken);

						return;
					} else if (xpcWrapUrl && childToken?.type === 'link_open') {
						// For plain links not supported by the linking platform, apply xpcWrapUrl (with full wrapping) to the href
						childToken.attrSet('href', xpcWrapUrl(url, false));
					}

					// pushes non link tokens as is
					processedTokenChildren.push(childToken);
				});
			}

			// set processed children to the output token
			outputToken.children = processedTokenChildren;

			// Keeps inline tokens that have children and all non-inline tokens.
			const blockTokensWithContent = tokensToAppend.filter(
				(t) => t.type !== 'inline' || t.children?.length,
			);
			tokens.push(...blockTokensWithContent);
		}

		// Remove empty paragraphs otherwise add it to the filteredTokens
		for (let i = 0; i < tokens.length; i++) {
			if (
				i < tokens.length - 1 &&
				tokens[i].type === 'paragraph_open' &&
				tokens[i + 1].type === 'paragraph_close'
			) {
				i = i + 1;
			} else {
				filteredTokens.push(tokens[i]);
			}
		}

		state.tokens = [...filteredTokens, ...embedTokens];

		return;
	};

export const createSmartLinksPlugin =
	(
		params: Parameters<typeof createSmartLinks>[0],
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
		shouldShowAsInline?: boolean,
		xpcWrapUrl?: (url: string, stripOnly: boolean) => string,
	) =>
	(md: MarkdownIt): void =>
		md.core.ruler.push(
			'inlineCard',
			createSmartLinks({ ...params, isExperimentEnabled, shouldShowAsInline, xpcWrapUrl }),
		);
